import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { FileButton } from '@/components/FileButton'
import { useObservableFocus } from '@/hooks/focus-hooks'
import { useDropzone } from '@/hooks/dropzone-hook'
import {
  useCallback,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react'
import { NEVER, type Observable } from 'rxjs'
import styles from './DocumentRequests.module.css'

export interface DocumentRequestsProps {
  isDisabled?: boolean
  focus$?: Observable<unknown>
  onReceiveFiles: (files: File[]) => unknown
  children?: ReactNode
}

export function DocumentRequests({
  isDisabled,
  focus$ = NEVER,
  onReceiveFiles,
  children,
}: DocumentRequestsProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  // Focus the first file button or anchor element we can find.
  const focus = useCallback(
    () =>
      (
        (rootRef.current?.querySelector('input[type=file]') as
          HTMLInputElement | undefined) ?? rootRef.current?.querySelector('a')
      )?.focus(),
    [],
  )
  useObservableFocus(focus$, focus)

  const receiveFiles = useCallback(
    (fileList: FileList) => {
      onReceiveFiles(Array.from(fileList))
    },
    [onReceiveFiles],
  )

  return (
    <div ref={rootRef}>
      {children}
      <AdditionalDocumentsUploader
        onChange={receiveFiles}
        isDisabled={isDisabled}
      />
    </div>
  )
}

interface AdditionalDocumentsUploaderProps {
  isDisabled?: boolean
  onFocus?: () => unknown
  onChange: (files: FileList) => void
}

function AdditionalDocumentsUploader({
  isDisabled,
  onFocus,
  onChange,
}: AdditionalDocumentsUploaderProps) {
  const { isOver, dropzoneProps } = useDropzone<HTMLDivElement>({
    onFilesDropped: onChange,
  })

  return (
    <div
      className={cx(
        styles.item,
        styles.small,
        styles.center,
        isOver && styles.dragOver,
      )}
      {...dropzoneProps}
    >
      {isOver ? (
        'Drop file to upload'
      ) : (
        <>
          <div className={styles.smallText}>Drag and drop files here or</div>
          <div className={styles.itemActions}>
            <FileButton
              multiple={true}
              isDisabled={isDisabled}
              onChange={isDisabled ? undefined : onChange}
            >
              <ItemAction withoutPipe isDisabled={isDisabled} onFocus={onFocus}>
                Upload Another File
              </ItemAction>
            </FileButton>
          </div>
        </>
      )}
    </div>
  )
}

export interface DocumentRequestItemProps {
  title: string
  /**
   * Title of the document filling this request, if any.
   */
  documentTitle: string | null
  uploadPercent: number | null
  canReceiveFiles: boolean
  isDisabled?: boolean
  readOnly: boolean
  onFocus?: () => unknown
  onReceiveFile: (file: File) => unknown
  onRemove: () => unknown
  onDownload: () => unknown
  onLink: () => unknown
  documentPicker: ReactNode
  confirmRemoval: ReactNode
  testId?: string
}

export function DocumentRequestItem({
  title,
  documentTitle,
  uploadPercent,
  canReceiveFiles,
  isDisabled,
  readOnly = false,
  onFocus,
  onReceiveFile,
  onRemove,
  onDownload,
  onLink,
  documentPicker,
  confirmRemoval,
  testId,
}: DocumentRequestItemProps) {
  const receiveFiles = useCallback(
    (items: FileList) =>
      canReceiveFiles &&
      items &&
      items.length >= 1 &&
      onReceiveFile(Array.from(items)[0]),
    [onReceiveFile, canReceiveFiles],
  )

  const { isOver, dropzoneProps } = useDropzone<HTMLDivElement>({
    onFilesDropped: receiveFiles,
  })
  const hasDocument = documentTitle !== null

  return (
    <div
      className={cx(styles.item, isOver && canReceiveFiles && styles.dragOver)}
      {...dropzoneProps}
    >
      <div className={styles.itemContent}>
        <p className={styles.itemTitle}>{title}</p>
        {uploadPercent !== null ? (
          <UploadProgress percent={uploadPercent} />
        ) : (
          <p
            className={cx(hasDocument ? styles.documentName : styles.help)}
            data-test={`${testId}-filename`}
          >
            {hasDocument
              ? documentTitle
              : isOver
                ? 'Drop file to upload'
                : !readOnly
                  ? 'Drag and drop your file here'
                  : ''}
          </p>
        )}
      </div>
      {uploadPercent === null && (
        <div>
          {hasDocument ? (
            <div className={styles.itemActions}>
              <ItemAction
                isDisabled={isDisabled && !readOnly}
                onFocus={onFocus}
                onPress={readOnly ? onDownload : onRemove}
                testId={`${testId}-remove`}
              >
                {readOnly ? 'Download' : 'Remove'}
              </ItemAction>
            </div>
          ) : (
            !readOnly && (
              <div className={styles.itemActions}>
                <FileButton
                  isDisabled={isDisabled}
                  onChange={isDisabled ? undefined : receiveFiles}
                  data-test={`${testId}-file-input`}
                >
                  <ItemAction isDisabled={isDisabled} onFocus={onFocus}>
                    Upload
                  </ItemAction>
                </FileButton>
                <ItemAction
                  onPress={onLink}
                  onFocus={onFocus}
                  isDisabled={isDisabled}
                  testId={`${testId}-link`}
                >
                  Link
                </ItemAction>
              </div>
            )
          )}
        </div>
      )}
      {documentPicker}
      {confirmRemoval}
    </div>
  )
}

interface ItemActionProps {
  children: ReactNode
  isDisabled?: boolean
  onPress?: () => unknown
  onFocus?: () => unknown
  withoutPipe?: boolean
  buttonRef?: MutableRefObject<HTMLButtonElement | null>
  testId?: string
}

function ItemAction({
  children,
  onPress,
  onFocus,
  buttonRef,
  isDisabled,
  withoutPipe,
  testId,
}: ItemActionProps) {
  return (
    <RawButton
      className={cx(
        styles.itemAction,
        isDisabled && styles.disabled,
        withoutPipe && styles.withoutPipe,
      )}
      isDisabled={isDisabled}
      onPress={() => onPress?.()}
      ref={buttonRef}
      onFocus={onFocus}
      testId={testId}
    >
      {children}
    </RawButton>
  )
}

interface UploadProgressProps {
  percent: number
}

export function UploadProgress({ percent }: UploadProgressProps) {
  return (
    <div className={styles.uploadProgressContainer}>
      <div className={styles.uploadProgressTrack}>
        <div
          className={styles.uploadProgressBar}
          style={{
            width: `${percent}%`,
          }}
        />
      </div>
    </div>
  )
}
